import { jsx } from "react/jsx-runtime";
import {
  BaseBoxShapeUtil,
  HTMLContainer,
  embedShapeMigrations,
  embedShapeProps,
  lerp,
  resizeBox,
  toDomPrecision,
  useColorMode,
  useIsEditing,
  useSvgExportContext,
  useValue
} from "@tldraw/editor";
import {
  DEFAULT_EMBED_DEFINITIONS,
  embedShapePermissionDefaults,
  unknownEmbedShapePermissionOverrides
} from "../../defaultEmbedDefinitions.mjs";
import { getCorrectedEmbedSize, getEmbedInfo } from "../../utils/embeds/embeds.mjs";
import { BookmarkShapeComponent } from "../bookmark/BookmarkShapeUtil.mjs";
import { getDisplayValues } from "../shared/getDisplayValues.mjs";
import { getRotatedBoxShadow } from "../shared/rotated-box-shadow.mjs";
const getSandboxPermissions = (permissions) => {
  return Object.entries(permissions).filter(([_perm, isEnabled]) => isEnabled).map(([perm]) => perm).join(" ");
};
class EmbedShapeUtil extends BaseBoxShapeUtil {
  static type = "embed";
  static props = embedShapeProps;
  static migrations = embedShapeMigrations;
  options = {
    embedDefinitions: DEFAULT_EMBED_DEFINITIONS,
    embedConfig: {},
    getDefaultDisplayValues() {
      return {
        showShadow: true
      };
    },
    getCustomDisplayValues() {
      return {};
    }
  };
  canEditWhileLocked(shape) {
    const result = this.getEmbedDefinition(shape.props.url);
    if (!result) return true;
    return result.definition.canEditWhileLocked ?? true;
  }
  static legacyEmbedDefinitions = null;
  /** @deprecated - Use `EmbedShapeUtil.configure({ embedDefinitions: [...] })` instead. */
  static setEmbedDefinitions(embedDefinitions) {
    EmbedShapeUtil.legacyEmbedDefinitions = embedDefinitions;
  }
  getEmbedDefs() {
    return EmbedShapeUtil.legacyEmbedDefinitions ?? this.options.embedDefinitions;
  }
  getEmbedDefinitions() {
    return this.getEmbedDefs();
  }
  getEmbedDefinition(url) {
    return getEmbedInfo(this.getEmbedDefs(), url, this.options.embedConfig);
  }
  /**
   * Resolve the embed's true aspect ratio (for definitions with `sizeToContentAspectRatio`) and
   * correct the shape's size to match. The dimensions come from the URL's OpenGraph metadata via
   * the editor's url asset handler (the same "unfurl" path bookmarks use). The correction is
   * applied without adding to the undo history.
   */
  async resolveAspectRatio(shape) {
    const { url } = shape.props;
    const definition = this.getEmbedDefinition(url)?.definition;
    if (!definition?.sizeToContentAspectRatio || !url) return;
    const resolvedRatio = await this.getContentAspectRatio(url);
    if (this.editor.isDisposed) return;
    const latest = this.editor.getShape(shape.id);
    if (!latest || latest.props.url !== url) return;
    const corrected = getCorrectedEmbedSize({
      w: latest.props.w,
      h: latest.props.h,
      resolvedRatio
    });
    if (!corrected) return;
    const { minWidth, minHeight } = this.getResizeMinBounds(latest);
    const clearFloor = Math.max(1, minWidth / corrected.w, minHeight / corrected.h);
    const targetW = corrected.w * clearFloor;
    const targetH = corrected.h * clearFloor;
    this.editor.run(
      () => {
        this.editor.resizeShape(
          latest.id,
          {
            x: targetW / latest.props.w,
            y: targetH / latest.props.h
          },
          { isAspectRatioLocked: false }
        );
      },
      { history: "ignore" }
    );
  }
  /**
   * Resolve the content's aspect ratio from the URL's OpenGraph metadata, fetched through the
   * editor's url asset handler (the "unfurl" service). Returns `undefined` if no usable dimensions
   * are available (e.g. no unfurl service is configured, or the page reports no image size).
   */
  async getContentAspectRatio(url) {
    try {
      const asset = await this.editor.getAssetForExternalContent({ type: "url", url });
      if (!asset || asset.type !== "bookmark") return void 0;
      const width = asset.meta.imageWidth;
      const height = asset.meta.imageHeight;
      if (typeof width === "number" && typeof height === "number" && width > 0 && height > 0) {
        return width / height;
      }
    } catch {
    }
    return void 0;
  }
  getText(shape) {
    return shape.props.url;
  }
  getAriaDescriptor(shape) {
    const embedInfo = this.getEmbedDefinition(shape.props.url);
    return embedInfo?.definition.title;
  }
  hideSelectionBoundsFg(shape) {
    return !this.canResize(shape);
  }
  canEdit(shape) {
    return true;
  }
  canResize(shape) {
    return this.getEmbedDefinition(shape.props.url)?.definition?.doesResize ?? true;
  }
  canEditInReadonly(shape) {
    return true;
  }
  getDefaultProps() {
    return {
      w: 300,
      h: 300,
      url: ""
    };
  }
  getGeometry(shape) {
    return super.getGeometry(shape);
  }
  isAspectRatioLocked(shape) {
    const embedInfo = this.getEmbedDefinition(shape.props.url);
    return embedInfo?.definition.isAspectRatioLocked ?? false;
  }
  /**
   * The minimum width/height a resize of `shape` is allowed to reach. When the embed's aspect ratio
   * is locked, the longer axis's minimum is inflated by the current aspect ratio so that shrinking
   * to the floor keeps the shorter axis at or above the base minimum.
   */
  getResizeMinBounds(shape) {
    const embedInfo = this.getEmbedDefinition(shape.props.url);
    let minWidth = embedInfo?.definition.minWidth ?? 200;
    let minHeight = embedInfo?.definition.minHeight ?? 200;
    if (this.isAspectRatioLocked(shape)) {
      const aspectRatio = shape.props.w / shape.props.h;
      if (aspectRatio > 1) {
        minWidth *= aspectRatio;
      } else {
        minHeight /= aspectRatio;
      }
    }
    return { minWidth, minHeight };
  }
  onResize(shape, info) {
    return resizeBox(shape, info, this.getResizeMinBounds(shape));
  }
  component(shape) {
    const svgExport = useSvgExportContext();
    const { w, h, url } = shape.props;
    const isEditing = useIsEditing(shape.id);
    const colorMode = useColorMode();
    const dv = getDisplayValues(this, shape, colorMode);
    const embedInfo = this.getEmbedDefinition(url);
    const isHoveringWhileEditingSameShape = useValue(
      "is hovering",
      () => {
        const { editingShapeId, hoveredShapeId } = this.editor.getCurrentPageState();
        if (editingShapeId && hoveredShapeId !== editingShapeId) {
          const editingShape = this.editor.getShape(editingShapeId);
          if (editingShape && this.editor.isShapeOfType(editingShape, "embed")) {
            return true;
          }
        }
        return false;
      },
      []
    );
    const pageRotation = this.editor.getShapePageTransform(shape).rotation();
    if (svgExport) {
      return /* @__PURE__ */ jsx(HTMLContainer, { className: "tl-embed-container", id: shape.id, children: /* @__PURE__ */ jsx(
        "div",
        {
          className: "tl-embed",
          style: {
            border: 0,
            boxShadow: dv.showShadow ? getRotatedBoxShadow(pageRotation) : "none",
            borderRadius: embedInfo?.definition.overrideOutlineRadius ?? 8,
            background: embedInfo?.definition.backgroundColor ?? "var(--tl-color-background)",
            width: w,
            height: h
          }
        }
      ) });
    }
    const isInteractive = isEditing || isHoveringWhileEditingSameShape;
    const isIframe = typeof window !== "undefined" && (window !== window.top || window.self !== window.parent);
    if (isIframe && embedInfo?.definition.type === "tldraw") return null;
    const sandbox = getSandboxPermissions({
      ...embedShapePermissionDefaults,
      ...(embedInfo ? embedInfo.definition.overridePermissions ?? {} : unknownEmbedShapePermissionOverrides)
    });
    if (embedInfo?.definition.type === "github_gist") {
      const idFromGistUrl = embedInfo.url.split("/").pop();
      if (!idFromGistUrl) throw Error("No gist id!");
      const gistSandbox = getSandboxPermissions({
        ...embedShapePermissionDefaults,
        ...(embedInfo?.definition?.overridePermissions ?? {}),
        "allow-same-origin": false
      });
      return /* @__PURE__ */ jsx(HTMLContainer, { className: "tl-embed-container", id: shape.id, children: /* @__PURE__ */ jsx(
        Gist,
        {
          id: idFromGistUrl,
          sandbox: gistSandbox,
          width: toDomPrecision(w),
          height: toDomPrecision(h),
          isInteractive,
          pageRotation,
          showShadow: dv.showShadow
        }
      ) });
    }
    const iframeSrc = embedInfo?.embedUrl ?? url;
    return /* @__PURE__ */ jsx(HTMLContainer, { className: "tl-embed-container", id: shape.id, children: iframeSrc ? /* @__PURE__ */ jsx(
      "iframe",
      {
        className: "tl-embed",
        sandbox,
        src: iframeSrc,
        width: toDomPrecision(w),
        height: toDomPrecision(h),
        draggable: false,
        frameBorder: "0",
        referrerPolicy: "strict-origin-when-cross-origin",
        tabIndex: isEditing ? 0 : -1,
        allowFullScreen: true,
        style: {
          border: 0,
          // Fill the container exactly. Relying on the fixed pixel width/height causes
          // the flex-centered iframe to be rounded independently from its box at
          // fractional sizes/zooms, leaving a 1-2px background sliver on one edge.
          width: "100%",
          height: "100%",
          pointerEvents: isInteractive ? "auto" : "none",
          // Fix for safari <https://stackoverflow.com/a/49150908>
          zIndex: isInteractive ? "" : "-1",
          boxShadow: dv.showShadow ? getRotatedBoxShadow(pageRotation) : "none",
          borderRadius: embedInfo?.definition?.overrideOutlineRadius ?? 8,
          background: embedInfo?.definition?.backgroundColor
        }
      }
    ) : /* @__PURE__ */ jsx(
      BookmarkShapeComponent,
      {
        url,
        h,
        rotation: pageRotation,
        assetId: null,
        showImageContainer: false
      }
    ) });
  }
  getIndicatorPath(shape) {
    const path = new Path2D();
    path.rect(0, 0, shape.props.w, shape.props.h);
    return path;
  }
  getInterpolatedProps(startShape, endShape, t) {
    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      w: lerp(startShape.props.w, endShape.props.w, t),
      h: lerp(startShape.props.h, endShape.props.h, t)
    };
  }
}
function Gist({
  id,
  sandbox,
  isInteractive,
  width,
  height,
  style,
  pageRotation,
  showShadow
}) {
  if (!id.match(/^[0-9a-f]+$/)) throw Error("No gist id!");
  return /* @__PURE__ */ jsx(
    "iframe",
    {
      className: "tl-embed",
      sandbox,
      draggable: false,
      width: toDomPrecision(width),
      height: toDomPrecision(height),
      frameBorder: "0",
      scrolling: "no",
      referrerPolicy: "strict-origin-when-cross-origin",
      tabIndex: isInteractive ? 0 : -1,
      style: {
        ...style,
        pointerEvents: isInteractive ? "all" : "none",
        // Fix for safari <https://stackoverflow.com/a/49150908>
        zIndex: isInteractive ? "" : "-1",
        boxShadow: showShadow ? getRotatedBoxShadow(pageRotation) : "none"
      },
      srcDoc: `
			<html>
				<head>
					<base target="_blank">
				</head>
				<body>
					<script src=${`https://gist.github.com/${id}.js`}></script>
					<style type="text/css">
						* { margin: 0px; }
						table { height: 100%; background-color: red; }
						.gist { background-color: none; height: 100%;  }
						.gist .gist-file { height: calc(100vh - 2px); padding: 0px; display: grid; grid-template-rows: 1fr auto; }
					</style>
				</body>
			</html>`
    }
  );
}
export {
  EmbedShapeUtil
};
//# sourceMappingURL=EmbedShapeUtil.mjs.map
